import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Meteor } from 'meteor/meteor';
import { useTracker } from 'meteor/react-meteor-data';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { toastOnError } from '/imports/ui/utils';
import { useFindOne } from '/imports/hooks';
import { Orgs } from '/imports/api/orgs';
import { DateInput } from '/imports/ui/DateInput';
import { FileInput } from '/imports/ui/FileInput';
import { FileMeta } from '/imports/types';
import { generateRealtyIncomeBills } from '/imports/api/realtyIncomeBilling';
import { getSolarBillsBatchStatus } from '/imports/api/solarBills';
import { Loading } from '/imports/ui/Loading';
import { maxSpreadsheetUploadBytes } from '/imports/api/files';

const RealtyIncomeBillsPageStyled = styled(Page)`
  .generated {
    display: inline-flex;
    flex-direction: column;
    justify-content: center;
  }
`;

/** Super-user form for batch-generating Realty Income solar bills from a ledger upload. */
export const RealtyIncomeBillsPage = () => {
  const currentUser = useTracker(() => Meteor.user());
  if (!currentUser?.isSuperUser) return <NotPermittedPage />;
  const orgId = currentUser.orgId;
  const org = useFindOne(() => Orgs.find(orgId), [orgId]);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [issuedDate, setIssuedDate] = useState<string>();
  const [dueDate, setDueDate] = useState<string>();
  const [lastBillDate, setLastBillDate] = useState<string>();
  const [ledger, setLedger] = useState<FileMeta>();
  const [error,] = useState<any>({});
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState({ total: 0, generated: 0 });
  const navigate = useNavigate();
  const generateBills = toastOnError(async () => {
    if (!startDate || !endDate || !issuedDate || !dueDate || !ledger) return;
    setGenerating(true);
    const batch = Date.now();
    const interval = setInterval(async () => {
      setStatus(await getSolarBillsBatchStatus.call({ batch }));
    }, 1500);
    await generateRealtyIncomeBills.call({ batch, startDate, endDate, issuedDate, dueDate, ledger, lastBillDate });
    clearInterval(interval);
    navigate(`/solar-bills?batch=${batch}`);
    setGenerating(false);
  });
  return (
    <RealtyIncomeBillsPageStyled>
      <PageHeading
        left={<h1>{org.name}</h1>}
      />
      <PageBody>
        <Form onSubmit={() => generateBills()}>
          <div className="field-group">
            <FormField field="startDate" error={error}>
              <label>{strings.StartDate()}</label>
              <DateInput value={startDate} onChange={(startDate) => setStartDate(startDate)} />
            </FormField>
            <FormField field="endDate" error={error}>
              <label>{strings.EndDate()}</label>
              <DateInput value={endDate} onChange={(endDate) => setEndDate(endDate)} />
            </FormField>
          </div>
          <div className="field-group">
            <FormField field="issuedDate" error={error}>
              <label>{strings.IssuedDate()}</label>
              <DateInput value={issuedDate} onChange={(issuedDate) => setIssuedDate(issuedDate)} />
            </FormField>
            <FormField field="dueDate" error={error}>
              <label>{strings.DueDate()}</label>
              <DateInput value={dueDate} onChange={(dueDate) => setDueDate(dueDate)} />
            </FormField>
            <FormField field="lastBillDate" error={error}>
              <label>{strings.LastBillDate()} ({strings.Optional()})</label>
              <DateInput value={lastBillDate} onChange={(lastBillDate) => setLastBillDate(lastBillDate)} />
            </FormField>
          </div>
          <div className="field-group">
            <FormField field="ledger" error={error}>
              <label>{strings.Ledger()}</label>
              <FileInput value={ledger} maxSize={maxSpreadsheetUploadBytes} onChange={(ledger) => setLedger(ledger)} disabled={!startDate || !endDate} />
            </FormField>
          </div>
          <div className="field-group">
            <Button type="submit" disabled={!startDate || !endDate || !issuedDate || !dueDate || !ledger}>{generating ? <Loading /> : strings.Submit()}</Button>
            {generating && status.total ? <span className="generated">{strings.Generated()} {status.generated} / {status.total}</span> : null}
          </div>
        </Form>
      </PageBody>
    </RealtyIncomeBillsPageStyled>
  );
};
